import React, { useState, useEffect } from 'react';
import i18next from 'core/i18n';
import { useMutation, useQuery } from '@apollo/client';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';

import { FinancingUser, FinancingRole, SalesRep } from '@indigo-ag/schema';
import {
  DialogContent,
  DialogActions,
  Button,
  useFlashMessage,
  DialogProps,
  Multiselect,
  SelectOption,
  SelectChangeEvent,
  Select,
  palettes
} from '@indigo-ag/component-module';

import { EnhancedDialog, createStylesHook, text, RoleNames } from 'shared';
import {
  financingUsers,
  getAllSalesReps,
  upsertFinancingUser as upsertFinancingUserMut,
  upsertSalesReps
} from 'features/admin/adminQueries';

const useStyles = createStylesHook({
  formRow: {
    margin: '0 0 40px'
  },
  label: {
    fontSize: '0.87500rem',
    fontWeight: 700,
    paddingBottom: '0.25rem',
    color: palettes.brand.gray[700]
  },
  formRowHeight: {
    minHeight: '150px'
  },
  userWrapper: {
    margin: '20px 0 40px'
  }
});

interface UpdateUserPreferencesDialogProps extends DialogProps {
  userToUpdate: FinancingUser | null;
  closeDialog: () => void;
  roles: FinancingRole[];
}

export const UpdateUserPreferencesDialog: React.FunctionComponent<UpdateUserPreferencesDialogProps> = ({
  userToUpdate,
  open,
  closeDialog,
  roles
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * State
   */
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [newRole, setNewRole] = useState<string | null>(null);
  const [newSalesReps, setNewSalesReps] = useState<string[]>(['']);

  // Preselecting users' preferences
  const initialSalesReps = userToUpdate?.preferences?.salesReps.map(sr => sr.id) || [''];
  const initialRole = userToUpdate?.role.id || null;
  useEffect(() => {
    setNewSalesReps(userToUpdate?.preferences?.salesReps.map(sr => sr.id) || ['']);
    setNewRole(userToUpdate?.role.id || null);
  }, [userToUpdate]);

  /**
   * Mutations
   */
  const [upsertFinancingUserFn] = useMutation(upsertFinancingUserMut);
  const [upsertSalesRepsFn] = useMutation(upsertSalesReps);

  /**
   * Queries
   */
  const { data: salesRepsData } = useQuery<{ getAllSalesReps: SalesRep[] }>(getAllSalesReps);
  const salesReps = salesRepsData?.getAllSalesReps || [];

  const selectableRoles: SelectOption[] = roles.map(role => ({
    name: role.name,
    value: role.id
  }));

  const selectableSalesReps: SelectOption[] = salesReps.map(sr => ({
    name: sr.name,
    value: sr.id
  }));

  const { addFlashMessage } = useFlashMessage();

  const preferencesToUpdate: any[] = [];
  const updateUserPreferences = () => {
    const userRoleChanged = initialRole !== newRole;

    const newSalesRepsCopy = newSalesReps.slice();
    const userSalesRepsChanged = JSON.stringify(initialSalesReps.sort()) !== JSON.stringify(newSalesRepsCopy.sort());

    if (!userToUpdate || (!userRoleChanged && !userSalesRepsChanged)) {
      return;
    }

    if (userRoleChanged) {
      preferencesToUpdate.push(
        upsertFinancingUserFn({
          awaitRefetchQueries: true,
          refetchQueries: [{ query: financingUsers }],
          variables: {
            data: {
              id: userToUpdate.id,
              role: newRole
            }
          }
        })
      );
    }

    if (userSalesRepsChanged) {
      preferencesToUpdate.push(
        upsertSalesRepsFn({
          awaitRefetchQueries: true,
          refetchQueries: [{ query: financingUsers }],
          variables: {
            salesReps: salesReps
              .filter(sr => newSalesReps.includes(sr.id))
              .map(sr => ({ id: sr.id, name: sr.name, salesforceUserId: sr.salesforceUserId })),
            creditAnalystUserId: userToUpdate.id
          }
        })
      );
    }

    setIsUpdatingUser(true);
    Promise.allSettled(preferencesToUpdate).then(results => {
      const rejectedResults = results.filter(r => r.status === 'rejected');

      if (rejectedResults.length) {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('userEditsFailed')),
          title: '',
          variant: 'error'
        });
      } else {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('userEditsSaved')),
          title: '',
          variant: 'success'
        });
      }

      setIsUpdatingUser(false);
      closeDialog();
    });
  };

  const changeRoleData = (e: SelectChangeEvent) => {
    setNewRole(e.target.value);
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={closeDialog}
      title={i18next.t('editUser')}
      dialogMinHeight="0"
      dialogMaxHeight="fit-content"
      dialogMaxWidth="35rem"
    >
      <DialogContent>
        <div className={classes.userWrapper}>
          <div className={classes.label}>{i18next.t('user')}</div>
          <div>{userToUpdate?.email || text.firstLetterUppercase(i18next.t('noEmail'))}</div>
        </div>
        <div className={classes.formRow}>
          <Select
            name="new-role"
            label={i18next.t('role')}
            placeholder={i18next.t('pleaseSelect')}
            value={newRole}
            onChange={changeRoleData}
            options={selectableRoles}
          />
        </div>
        {initialRole === RoleNames.FINANCING_ADMIN_BR_CREDIT ? (
          <div className={`${classes.formRow} ${classes.formRowHeight}`}>
            <Multiselect
              filterable
              faIconBefore={faSearch}
              label={text.toTitleCase(i18next.t('salesRep'))}
              name="new-sales-reps"
              placeholder={i18next.t('pleaseSelect')}
              options={selectableSalesReps}
              value={newSalesReps}
              onSelectionChanged={setNewSalesReps}
            />
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} type="button" size="small" variant="secondary">
          {i18next.t('cancel')}
        </Button>
        <Button onClick={updateUserPreferences} type="submit" size="small" busy={isUpdatingUser} disabled={!newRole}>
          {i18next.t('save')}
        </Button>
      </DialogActions>
    </EnhancedDialog>
  );
};
